// =============================================================================
// Library Single Upload Modal — batchless quick upload + variant upload
// =============================================================================
// One file, no batch: pick → hash → precheck (conflict panel with rename /
// side-by-side preview) → single-init → PUT → complete(assetId, v1). Used from
// the Assets page (quick upload) and from asset detail (upload a variant —
// `primaryAssetId` preset links the new asset at creation). On success the
// caller decides where to go (assets page navigates to the new asset detail).
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { Button, Input, Modal, ModalFooter } from '@tkpod/ui';
import {
  LIBRARY_ALLOWED_MIME_TYPES,
  LIBRARY_UPLOAD_DEFAULT_MAX_FILE_MB,
  type LibraryUploadPrecheckFileResult,
} from '@tkpod/shared';
import { toast } from '@/lib/toast';
import { completeAssetVersion, patchAsset, precheckUpload, singleUploadInit } from './api';

const ACCEPT = '.png,.jpg,.jpeg,.webp';
const MAX_FILE_MB = LIBRARY_UPLOAD_DEFAULT_MAX_FILE_MB;
const PUT_MAX_ATTEMPTS = 3;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const CONFLICT_REASON_LABEL: Record<string, string> = {
  sku: 'SKU exists',
  fileName: 'Same file name',
  contentHash: 'Identical bytes',
};

async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** PUT with backoff on network/5xx. Singles finish well inside the presign
 * TTL, so there is no refresh-url leg here (that route is batch-scoped). */
async function putFile(
  url: string,
  file: File,
  onProgress: (pct: number) => void,
): Promise<void> {
  const attempt = () =>
    new Promise<number | null>((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.timeout = 120_000;
      xhr.upload.onprogress = (e) =>
        e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
      xhr.onload = () => resolve(xhr.status);
      xhr.onerror = () => resolve(null);
      xhr.ontimeout = () => resolve(null);
      xhr.send(file);
    });
  let last: number | null = null;
  for (let i = 0; i < PUT_MAX_ATTEMPTS; i++) {
    const status = await attempt();
    if (status !== null && status >= 200 && status < 300) return;
    last = status;
    if (i < PUT_MAX_ATTEMPTS - 1) {
      onProgress(0);
      await sleep(1000 * (i + 1));
    }
  }
  throw new Error(`Upload failed after ${PUT_MAX_ATTEMPTS} attempts (${last ?? 'network error'})`);
}

type Phase = 'idle' | 'hashing' | 'checking' | 'ready' | 'conflict' | 'uploading';

export interface LibrarySingleUploadModalProps {
  open: boolean;
  onClose: () => void;
  /** Preset primary — variant-upload mode (asset detail page). */
  primaryAssetId?: string;
  /** Sku of the primary, shown in the variant-mode header. */
  primarySku?: string;
  /** Called with the new asset id after complete succeeds. */
  onUploaded: (assetId: string) => void;
}

export function LibrarySingleUploadModal({
  open,
  onClose,
  primaryAssetId,
  primarySku,
  onUploaded,
}: LibrarySingleUploadModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [contentHash, setContentHash] = useState<string | undefined>();
  const [phase, setPhase] = useState<Phase>('idle');
  const [check, setCheck] = useState<LibraryUploadPrecheckFileResult | null>(null);
  const [skuOverride, setSkuOverride] = useState('');
  const [title, setTitle] = useState('');
  const [variantLabel, setVariantLabel] = useState('');
  const [progress, setProgress] = useState(0);
  const runRef = useRef(0);
  const variantMode = Boolean(primaryAssetId);

  const reset = () => {
    runRef.current += 1;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setContentHash(undefined);
    setPhase('idle');
    setCheck(null);
    setSkuOverride('');
    setTitle('');
    setVariantLabel('');
    setProgress(0);
  };

  // Reset whenever the modal is (re)opened for a clean slate. `reset` is
  // stable in behavior (only touches state/refs), so `open` is the only dep.
  useEffect(() => {
    if (open) reset();
  }, [open]);

  const runPrecheck = async (f: File, hash: string | undefined, sku: string | undefined) => {
    const token = ++runRef.current;
    setPhase('checking');
    try {
      const res = await precheckUpload({
        files: [{ name: f.name, contentHash: hash, sku: sku || undefined }],
      });
      if (runRef.current !== token) return;
      const result = res.files[0];
      setCheck(result);
      // Sku matches always block (init would 409). Name/bytes-only matches
      // block until the user renames — the rename IS the explicit "upload as
      // a new asset" decision (server only enforces sku uniqueness).
      const skuConflict = result.matches.some((m) => m.matchedOn.includes('sku'));
      const blocked = skuConflict || (result.matches.length > 0 && !sku);
      setPhase(blocked ? 'conflict' : 'ready');
    } catch {
      if (runRef.current !== token) return;
      // Advisory — init re-validates strictly.
      setCheck(null);
      setPhase('ready');
    }
  };

  const pickFile = async (list: FileList | null) => {
    const f = list?.[0];
    if (!f) return;
    if (!(LIBRARY_ALLOWED_MIME_TYPES as readonly string[]).includes(f.type)) {
      toast('Only PNG/JPEG/WebP allowed', 'warning');
      return;
    }
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      toast(`File exceeds ${MAX_FILE_MB}MB`, 'warning');
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setSkuOverride('');
    setPhase('hashing');
    let hash: string | undefined;
    try {
      hash = await sha256Hex(f);
    } catch {
      hash = undefined;
    }
    setContentHash(hash);
    await runPrecheck(f, hash, undefined);
  };

  const applyRename = () => {
    if (!file || !skuOverride.trim()) return;
    void runPrecheck(file, contentHash, skuOverride.trim());
  };

  const upload = async () => {
    if (!file || phase !== 'ready') return;
    setPhase('uploading');
    setProgress(0);
    try {
      const init = await singleUploadInit({
        file: {
          name: file.name,
          size: file.size,
          mimeType: file.type,
          sku: skuOverride.trim() || undefined,
        },
        primaryAssetId,
        variantLabel: variantMode ? variantLabel.trim() || undefined : undefined,
      });
      await putFile(init.uploadUrl, file, setProgress);
      await completeAssetVersion(init.assetId, 1);
      // Optional metadata — best-effort, the asset exists either way.
      if (title.trim()) {
        await patchAsset(init.assetId, { title: title.trim() }).catch(() => undefined);
      }
      toast('Upload complete — processing started', 'success');
      onUploaded(init.assetId);
    } catch (e) {
      toast((e as Error).message, 'error');
      // Re-check so a raced 409 renders the conflict panel again.
      setPhase('checking');
      void runPrecheck(file, contentHash, skuOverride.trim() || undefined);
    }
  };

  const firstMatch = check?.matches[0];
  const busy = phase === 'uploading';

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) { reset(); onClose(); } }}
      title={variantMode ? `Upload variant of “${primarySku ?? ''}”` : 'Quick upload'}
    >
      <div className="space-y-4">
        {/* File picker */}
        {!file ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full rounded-xl border-2 border-dashed border-brand-800 px-6 py-10 text-center hover:border-brand-600"
          >
            <p className="text-sm font-medium text-text-primary">Choose an image</p>
            <p className="mt-1 text-xs text-text-tertiary">PNG, JPEG or WebP · max {MAX_FILE_MB}MB</p>
          </button>
        ) : (
          <div className="flex items-center gap-3 rounded-xl border border-brand-900 px-4 py-3">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-[#bdbdbd]">
              {previewUrl && <img src={previewUrl} alt={file.name} className="max-h-full max-w-full object-contain" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-text-primary">{file.name}</p>
              <p className="text-xs text-text-tertiary">
                {check ? (
                  <>sku <span className="font-mono">{check.derivedSku}</span></>
                ) : phase === 'hashing' ? 'Hashing…' : phase === 'checking' ? 'Checking…' : null}
              </p>
              {phase === 'uploading' && (
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-tertiary">
                  <div className="h-full bg-brand-500 transition-all" style={{ width: `${progress}%` }} />
                </div>
              )}
            </div>
            {!busy && (
              <Button variant="ghost" size="sm" onClick={() => { reset(); }}>
                Change
              </Button>
            )}
          </div>
        )}
        <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => void pickFile(e.target.files)} />

        {/* Conflict panel */}
        {phase === 'conflict' && check && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/5 px-4 py-3">
            <p className="text-sm font-semibold text-red-400">This file conflicts with an existing asset</p>
            <div className="mt-2 flex flex-wrap gap-1">
              {firstMatch?.matchedOn.map((r) => (
                <span key={r} className="rounded border border-red-500/40 bg-red-500/10 px-1.5 py-0.5 text-[11px] text-red-400">
                  {CONFLICT_REASON_LABEL[r] ?? r}
                </span>
              ))}
            </div>
            {firstMatch && (
              <div className="mt-3 flex items-center gap-3">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-[#bdbdbd]">
                  {firstMatch.thumbUrl ? (
                    <img src={firstMatch.thumbUrl} alt={firstMatch.sku} className="max-h-full max-w-full object-contain" />
                  ) : (
                    <span className="text-[10px] text-text-tertiary">No preview</span>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm text-text-primary">
                    In library: <span className="font-mono">{firstMatch.sku}</span>
                  </p>
                  <p className="truncate text-xs text-text-tertiary">{firstMatch.fileName} · {firstMatch.status}</p>
                  <a
                    href={`/library/assets/${firstMatch.assetId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-medium text-brand-400 hover:underline"
                  >
                    Open existing asset ↗
                  </a>
                </div>
              </div>
            )}
            <div className="mt-3 flex items-center gap-2">
              <Input
                placeholder="New sku…"
                value={skuOverride}
                onChange={(e) => setSkuOverride(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyRename()}
              />
              <Button variant="outline" size="sm" disabled={!skuOverride.trim()} onClick={applyRename}>
                Rename
              </Button>
            </div>
          </div>
        )}

        {/* Renamed past a name/bytes match: explicit "new asset" decision. */}
        {phase === 'ready' && skuOverride.trim() && (check?.matches.length ?? 0) > 0 && (
          <p className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-xs text-amber-600">
            Matches “{check!.matches[0].sku}” ({check!.matches[0].matchedOn.includes('contentHash') ? 'identical bytes' : 'same file name'}) — uploading as a new asset
          </p>
        )}

        {/* Auto-link hint (quick-upload mode only; variant mode links explicitly) */}
        {!variantMode && phase === 'ready' && check?.variantSuggestion && (
          <p className="rounded border border-purple-400/40 bg-purple-500/10 px-2 py-1 text-xs text-purple-500">
            Variant of “{check.variantSuggestion.baseSku}” — will be auto-linked (undo from the asset page)
          </p>
        )}

        {/* Optional metadata */}
        {file && phase !== 'conflict' && (
          <div className="space-y-3">
            <Input
              label="Title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={busy}
            />
            {variantMode && (
              <Input
                label="Variant label (optional)"
                placeholder="e.g. w, black, xl"
                value={variantLabel}
                onChange={(e) => setVariantLabel(e.target.value)}
                disabled={busy}
              />
            )}
          </div>
        )}
      </div>

      <ModalFooter>
        <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={busy}>
          Cancel
        </Button>
        <Button
          loading={busy}
          disabled={!file || phase !== 'ready'}
          title={phase === 'conflict' ? 'Resolve the conflict first (rename or change file)' : undefined}
          onClick={() => void upload()}
        >
          Upload
        </Button>
      </ModalFooter>
    </Modal>
  );
}
